import { createSlice } from '@reduxjs/toolkit';
import {
  getTrucksInPlantByWorkshopData,
  getTrucksInParkingData,
  getUnloadingPointsAverageDlzData,
  getTrucksInPlantByUnloadingBufferData,
  getTrucksInPlantWaitingToArriveData,
  getTrucksInPlantUrgentData
} from './async';
import { TrucksInfoState } from './types';
import {
  TrucksInArea,
  TrucksInPlantByPointInfo,
  UnloadingPointsAverageDlzInfo
} from 'modules/inbound/entriesST12/domain/TrucksInfo';

const emptyTrucksInArea = new TrucksInArea();
const emptyUnloadingPointsAverageDlzInfo = new UnloadingPointsAverageDlzInfo();
const emptyTrucksInPlantByPointInfo = new TrucksInPlantByPointInfo();

export const initialState: TrucksInfoState = {
  trucksInPlantByWorkshopData: emptyTrucksInArea,
  trucksInParkingData: emptyTrucksInArea,
  unloadingPointsAverageDlzData: emptyUnloadingPointsAverageDlzInfo,
  trucksInPlantByUnloadingBufferData: emptyTrucksInPlantByPointInfo,
  trucksInPlantWaitingToArriveData: emptyTrucksInPlantByPointInfo,
  trucksInPlantUrgentData: emptyTrucksInArea,
  trucksInPlantByWorkshopRequestStatus: '',
  trucksInParkingRequestStatus: '',
  unloadingPointsAverageDlzRequestStatus: '',
  trucksInPlantByUnloadingBufferRequestStatus: '',
  trucksInPlantWaitingToArriveRequestStatus: '',
  trucksInPlantUrgentRequestStatus: ''
};

const trucksInfoSlice = createSlice({
  name: 'trucksInfoSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      getTrucksInPlantByWorkshopData.fulfilled,
      (state, action) => {
        state.trucksInPlantByWorkshopData = action.payload || emptyTrucksInArea;
        state.trucksInPlantByWorkshopRequestStatus = action.meta.requestStatus;
      }
    );
    builder.addCase(getTrucksInPlantByWorkshopData.pending, (state, action) => {
      state.trucksInPlantByWorkshopRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(
      getTrucksInPlantByWorkshopData.rejected,
      (state, action) => {
        state.trucksInPlantByWorkshopRequestStatus = action.meta.requestStatus;
      }
    );

    builder.addCase(getTrucksInParkingData.fulfilled, (state, action) => {
      state.trucksInParkingData = action.payload || emptyTrucksInArea;
      state.trucksInParkingRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getTrucksInParkingData.pending, (state, action) => {
      state.trucksInParkingRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getTrucksInParkingData.rejected, (state, action) => {
      state.trucksInParkingRequestStatus = action.meta.requestStatus;
    });

    builder.addCase(
      getUnloadingPointsAverageDlzData.fulfilled,
      (state, action) => {
        state.unloadingPointsAverageDlzData =
          action.payload || emptyUnloadingPointsAverageDlzInfo;
        state.unloadingPointsAverageDlzRequestStatus =
          action.meta.requestStatus;
      }
    );
    builder.addCase(
      getUnloadingPointsAverageDlzData.pending,
      (state, action) => {
        state.unloadingPointsAverageDlzRequestStatus =
          action.meta.requestStatus;
      }
    );
    builder.addCase(
      getUnloadingPointsAverageDlzData.rejected,
      (state, action) => {
        state.unloadingPointsAverageDlzRequestStatus =
          action.meta.requestStatus;
      }
    );

    builder.addCase(
      getTrucksInPlantByUnloadingBufferData.fulfilled,
      (state, action) => {
        state.trucksInPlantByUnloadingBufferData =
          action.payload || emptyTrucksInPlantByPointInfo;
        state.trucksInPlantByUnloadingBufferRequestStatus =
          action.meta.requestStatus;
      }
    );
    builder.addCase(
      getTrucksInPlantByUnloadingBufferData.pending,
      (state, action) => {
        state.trucksInPlantByUnloadingBufferRequestStatus =
          action.meta.requestStatus;
      }
    );
    builder.addCase(
      getTrucksInPlantByUnloadingBufferData.rejected,
      (state, action) => {
        state.trucksInPlantByUnloadingBufferRequestStatus =
          action.meta.requestStatus;
      }
    );

    builder.addCase(
      getTrucksInPlantWaitingToArriveData.fulfilled,
      (state, action) => {
        state.trucksInPlantWaitingToArriveData =
          action.payload || emptyTrucksInPlantByPointInfo;
        state.trucksInPlantWaitingToArriveRequestStatus =
          action.meta.requestStatus;
      }
    );
    builder.addCase(
      getTrucksInPlantWaitingToArriveData.pending,
      (state, action) => {
        state.trucksInPlantWaitingToArriveRequestStatus =
          action.meta.requestStatus;
      }
    );
    builder.addCase(
      getTrucksInPlantWaitingToArriveData.rejected,
      (state, action) => {
        state.trucksInPlantWaitingToArriveRequestStatus =
          action.meta.requestStatus;
      }
    );

    builder.addCase(getTrucksInPlantUrgentData.fulfilled, (state, action) => {
      state.trucksInPlantUrgentData = action.payload || emptyTrucksInArea;
      state.trucksInPlantUrgentRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getTrucksInPlantUrgentData.pending, (state, action) => {
      state.trucksInPlantUrgentRequestStatus = action.meta.requestStatus;
    });
    builder.addCase(getTrucksInPlantUrgentData.rejected, (state, action) => {
      state.trucksInPlantUrgentRequestStatus = action.meta.requestStatus;
    });
  }
});

export const getTrucksInPlantByWorkshop = (state) =>
  state.trucksInfoSliceState.trucksInPlantByWorkshopData;
export const getTrucksInParking = (state) =>
  state.trucksInfoSliceState.trucksInParkingData;
export const getUnloadingPointsAverageDlz = (state) =>
  state.trucksInfoSliceState.unloadingPointsAverageDlzData;
export const getTrucksInPlantByUnloadingBuffer = (state) =>
  state.trucksInfoSliceState.trucksInPlantByUnloadingBufferData;
export const getTrucksInPlantWaitingToArrive = (state) =>
  state.trucksInfoSliceState.trucksInPlantWaitingToArriveData;
export const getTrucksInPlantUrgent = (state) =>
  state.trucksInfoSliceState.trucksInPlantUrgentData;

export const getTrucksInPlantUrgentRequestStatusIsPending = (state) =>
  state.trucksInfoSliceState.trucksInPlantUrgentRequestStatus === 'pending';
export const getTrucksInPlantUrgentRequestStatusHasError = (state) =>
  state.trucksInfoSliceState.trucksInPlantUrgentRequestStatus === 'rejected';
export const getTrucksInPlantUrgentRequestStatusIsSucessfull = (state) =>
  state.trucksInfoSliceState.trucksInPlantUrgentRequestStatus === 'fulfilled';

export const getTrucksInPlantByWorkshopRequestStatusIsPending = (state) =>
  state.trucksInfoSliceState.trucksInPlantByWorkshopRequestStatus === 'pending';
export const getTrucksInPlantByWorkshopRequestStatusHasError = (state) =>
  state.trucksInfoSliceState.trucksInPlantByWorkshopRequestStatus ===
  'rejected';
export const getTrucksInPlantByWorkshopRequestStatusIsSucessfull = (state) =>
  state.trucksInfoSliceState.trucksInPlantByWorkshopRequestStatus ===
  'fulfilled';

export const getTrucksInParkingRequestStatusIsPending = (state) =>
  state.trucksInfoSliceState.trucksInParkingRequestStatus === 'pending';
export const getTrucksInParkingRequestStatusHasError = (state) =>
  state.trucksInfoSliceState.trucksInParkingRequestStatus === 'rejected';
export const getTrucksInParkingRequestStatusIsSucessfull = (state) =>
  state.trucksInfoSliceState.trucksInParkingRequestStatus === 'fulfilled';

export const getUnloadingPointsAverageDlzRequestStatusIsPending = (state) =>
  state.trucksInfoSliceState.unloadingPointsAverageDlzRequestStatus ===
  'pending';
export const getUnloadingPointsAverageDlzRequestStatusHasError = (state) =>
  state.trucksInfoSliceState.unloadingPointsAverageDlzRequestStatus ===
  'rejected';
export const getUnloadingPointsAverageDlzRequestStatusIsSucessfull = (state) =>
  state.trucksInfoSliceState.unloadingPointsAverageDlzRequestStatus ===
  'fulfilled';

export const getTrucksInPlantByUnloadingBufferRequestStatusIsPending = (
  state
) =>
  state.trucksInfoSliceState.trucksInPlantByUnloadingBufferRequestStatus ===
  'pending';
export const getTrucksInPlantByUnloadingBufferRequestStatusHasError = (state) =>
  state.trucksInfoSliceState.trucksInPlantByUnloadingBufferRequestStatus ===
  'rejected';

export const getTrucksInPlantByUnloadingBufferRequestStatusIsSucessfull = (
  state
) =>
  state.trucksInfoSliceState.trucksInPlantByUnloadingBufferRequestStatus ===
  'fulfilled';

export const getTrucksInPlantWaitingToArriveRequestStatusIsPending = (state) =>
  state.trucksInfoSliceState.trucksInPlantWaitingToArriveRequestStatus ===
  'pending';
export const getTrucksInPlantWaitingToArriveRequestStatusHasError = (state) =>
  state.trucksInfoSliceState.trucksInPlantWaitingToArriveRequestStatus ===
  'rejected';
export const getTrucksInPlantWaitingToArriveRequestStatusIsSucessfull = (
  state
) =>
  state.trucksInfoSliceState.trucksInPlantWaitingToArriveRequestStatus ===
  'fulfilled';

/**export const {
  updateSelectedHourEntryType,
  updateSelectedDaysEntryType,
  updateSelectedHour
} = trucksInfoSlice.actions;*/
export default trucksInfoSlice.reducer;
